const PUBLIC_DEMO_STATE_VERSION = 3;
const PUBLIC_DEMO_STORAGE_PREFIX = 'aprendos:public-demo:';

const EXERCISES_BY_LANGUAGE = {
  en: [
    {
      id: 'en-survival-factors',
      prompt: 'Which two factors helped Vesna survive the fall?',
      options: [
        {
          id: 'cart-and-snow',
          label: 'A drinks cart pinned her in the galley, and deep snow cushioned the crash',
        },
        {
          id: 'parachute-and-water',
          label: 'She had a hidden parachute, and the plane landed in water',
        },
        {
          id: 'seat-and-rescuers',
          label: 'She stayed in her seat, and rescuers caught the fuselage',
        },
      ],
      correctOptionId: 'cart-and-snow',
    },
    {
      id: 'en-survival-after-recovery',
      prompt: 'What did Vesna do after she recovered?',
      options: [
        {
          id: 'same-airline',
          label: 'She returned to the same airline but did not fly again',
        },
        {
          id: 'different-airline',
          label: 'She became a pilot for a different airline',
        },
        {
          id: 'left-aviation',
          label: 'She refused to work in aviation again',
        },
      ],
      correctOptionId: 'same-airline',
    },
  ],
  es: [
    {
      id: 'es-besar-porcentaje',
      prompt: 'Según los antropólogos, ¿aproximadamente qué porcentaje de las culturas practica el beso romántico en los labios?',
      options: [
        { id: '46', label: 'Alrededor del 46 %' },
        { id: '75', label: 'Alrededor del 75 %' },
        { id: '100', label: 'Prácticamente el 100 %' },
      ],
      correctOptionId: '46',
    },
    {
      id: 'es-besar-utilidad-cultural',
      prompt: 'Según los locutores, ¿por qué es útil entender estas diferencias culturales?',
      options: [
        { id: 'relacionarse', label: 'Ayuda al viajar o hablar con personas de otros países' },
        { id: 'forma-unica', label: 'Permite elegir una única forma correcta de mostrar cariño' },
        { id: 'universal', label: 'Demuestra que besar en los labios es una práctica universal' },
      ],
      correctOptionId: 'relacionarse',
    },
  ],
  no: [
    {
      id: 'no-utsettelse-folelser',
      prompt: 'Hva sier forskerne at utsettelse først og fremst handler om?',
      options: [
        { id: 'ubehag', label: 'Å unngå ubehagelige følelser' },
        { id: 'tid', label: 'Å ha for lite tid' },
        { id: 'latskap', label: 'Å være lat' },
      ],
      correctOptionId: 'ubehag',
    },
    {
      id: 'no-utsettelse-fem-minutter',
      prompt: 'Hva foreslår Solveig at du sier til deg selv i stedet for «Jeg skal skrive hele rapporten»?',
      options: [
        { id: 'fem-minutter', label: 'Jeg skal skrive i fem minutter' },
        { id: 'rydde', label: 'Jeg skal rydde hele leiligheten' },
        { id: 'vente', label: 'Jeg skal vente til oppgaven føles lettere' },
      ],
      correctOptionId: 'fem-minutter',
    },
  ],
};

function normalizeLanguage(language) {
  const normalized = String(language || '').trim().toLowerCase();
  if (normalized.startsWith('es')) return 'es';
  if (normalized.startsWith('no') || normalized.startsWith('nb') || normalized.startsWith('nn')) return 'no';
  return 'en';
}

export function getPublicDemoExercises(language) {
  return EXERCISES_BY_LANGUAGE[normalizeLanguage(language)];
}

export function getPublicDemoStorageKey(demoId) {
  return `${PUBLIC_DEMO_STORAGE_PREFIX}${String(demoId || 'homepage_podcast')}`;
}

export function createInitialPublicDemoState({ demoId, language, level }) {
  return {
    version: PUBLIC_DEMO_STATE_VERSION,
    demoId,
    language: normalizeLanguage(language),
    level: level || '',
    stage: 'listen',
    audioTime: 0,
    audioProgress: 0,
    currentQuestionIndex: 0,
    answers: [],
    score: 0,
    completed: false,
  };
}

export function restorePublicDemoState(storage, demo) {
  const initialState = createInitialPublicDemoState(demo);
  if (!storage) return initialState;

  try {
    const stored = JSON.parse(storage.getItem(getPublicDemoStorageKey(demo.demoId)));
    if (
      stored?.version !== PUBLIC_DEMO_STATE_VERSION
      || stored.demoId !== initialState.demoId
      || stored.language !== initialState.language
      || !['listen', 'exercise', 'result'].includes(stored.stage)
      || !Array.isArray(stored.answers)
    ) {
      return initialState;
    }

    return {
      ...initialState,
      ...stored,
      audioTime: Math.max(0, Number(stored.audioTime) || 0),
      audioProgress: Math.min(100, Math.max(0, Number(stored.audioProgress) || 0)),
      currentQuestionIndex: Math.max(0, Number(stored.currentQuestionIndex) || 0),
      score: Math.max(0, Number(stored.score) || 0),
      completed: Boolean(stored.completed),
    };
  } catch {
    return initialState;
  }
}

export function persistPublicDemoState(storage, state) {
  if (!storage || !state?.demoId) return;

  try {
    storage.setItem(getPublicDemoStorageKey(state.demoId), JSON.stringify(state));
  } catch {
    // Demo progress is helpful but must never block playback when storage is unavailable.
  }
}

export function answerPublicDemoQuestion(state, question, selectedOptionId, questionCount) {
  const isCorrect = selectedOptionId === question.correctOptionId;
  const answers = [
    ...state.answers,
    {
      questionId: question.id,
      selectedOptionId,
      isCorrect,
    },
  ];
  const currentQuestionIndex = state.currentQuestionIndex + 1;
  const completed = currentQuestionIndex >= questionCount;

  return {
    ...state,
    answers,
    score: state.score + (isCorrect ? 1 : 0),
    currentQuestionIndex,
    stage: completed ? 'result' : 'exercise',
    completed,
  };
}
